import { useEffect, useState } from "react";
import RadioGroup from "../../components/Form/RadioGroup";
import Search from "../../components/Form/Search";
import Button from "../../components/Form/Button";
import Modal from "../../components/Elements/Modal";
import AuthorItem from "../../components/Items/AuthorItem";
import AddAuthors from "../../components/Modals/AddAuthors";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Pagination from "../../components/Elements/Pagination";
import { detailsAuthor, listAuthor } from "../../helpers/api_helper";
import moment from "moment";
import Select from "react-select";

const AuthorsList = () => {
  // Query Client instance
  const queryClient = useQueryClient();

  // For Managing Modals
  const [isAddModal, setIsAddModal] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);

  // For pagination
  const [perPages, setPerPages] = useState([
    { label: "10 (Default)", value: 10 },
    { label: 20, value: 20 },
    { label: 50, value: 50 },
    { label: 100, value: 100 },
    { label: "All", value: 999999 },
  ]);
  const [selectedPageOpt, setSelectedPageOpt] = useState([]);

  // For Filtering Data 🔍
  const [keyword, setKeyword] = useState("");
  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState("");

  // To fetch Details of specific items when it needs to be edited
  const [editId, setEditId] = useState("");

  // Static status Radio buttons
  const [statusStatic, setStatusStatic] = useState([
    { _id: "", name: "All", color: "success", current: true },
    { _id: true, name: "Published", color: "success", current: false },
    { _id: false, name: "Draft", color: "warning", current: false },
  ]);

  // Dynamic query key for Authors 🗝️
  const queryAuthors = ["authors", keyword, status, limit, offset];

  // For opening Add Modal althogh not mandetory
  const addModal = () => {
    setIsAddModal(true);
  };

  // For fetching details of item
  const {
    data: details,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["details", editId],
    queryFn: async () => detailsAuthor({ id: editId }),
    enabled: false,
  });

  // Refetch when editId updates
  useEffect(() => {
    if (editId !== "") {
      refetch();
    }
  }, [editId, refetch]);

  // Author List Component
  function AuthorList() {
    // Custom hook for fetching Author list
    function useAuthor() {
      return useQuery({
        queryKey: queryAuthors,
        queryFn: () =>
          listAuthor({
            keyword: keyword,
            sortByValue: 1,
            status: status,
            limit: limit,
            offset: offset,
          }),
        keepPreviousData: true,
      });
    }

    const { data: authors, error, isFetching } = useAuthor();

    // Handling loading state 😑
    if (isFetching) {
      return (
        <div className="noresult">
          <div className="text-center">
            <div className="fa-solid fa-circle-notch fa-spin"></div>
          </div>
        </div>
      );
    }

    // Handling error state 🥴
    if (error) {
      return <div>Something went wrong 🫥</div>;
    }

    // Check if their is no data 🤨
    else if (authors.data.length === 0) {
      return (
        <div className="noresult">
          <div className="text-center">
            <h5 className="mt-2">Sorry! No Result Found</h5>
          </div>
        </div>
      );
    }

    // Renders the list
    else {
      return (
        // For Pagination
        <>
          {authors.data.length > 0 &&
            authors.data.map((item, index) => {
              return (
                <AuthorItem
                  key={item._id}
                  index={index}
                  AuthorId={item._id}
                  name={item?.name}
                  descriptions={item?.description}
                  createdDate={moment(new Date(item?.created_at)).format(
                    "Do MMM YY"
                  )}
                  updatedDate={moment(new Date(item?.updated_at)).format(
                    "Do MMM YY"
                  )}
                  query={queryAuthors}
                  status={item?.status}
                  detailsFetch={refetch}
                  setEditId={setEditId}
                  setEditModal={setIsEditModal}
                />
              );
            })}

          <Pagination
            count={authors.count}
            limit={limit}
            offset={offset}
            setOffset={setOffset}
          />
        </>
      );
    }
  }

  return (
    <>
      <div className="page-filter mb-3">
        <div className="input-filters">
          <div className="input-items">
            {perPages && (
              <div className="input">
                <Select
                  className="select-tag"
                  isMulti={false}
                  isClearable={true}
                  options={perPages}
                  value={selectedPageOpt || []}
                  placeholder={"Per Page"}
                  onChange={(e) => {
                    setLimit(e.value);
                    setSelectedPageOpt(e);
                  }}
                />
              </div>
            )}
          </div>
          <div className="input-items">
            <Search
              placeholder={"Search..."}
              state={keyword}
              setState={setKeyword}
            />
          </div>

          <div className="input-items items-end ms-auto gap-3">
            <Button
              classes={"px-2"}
              icon={"fa-regular fa-arrow-rotate-right"}
              iconPosition={"left"}
              functions={() => {
                queryClient.invalidateQueries(queryAuthors);
                queryClient.removeQueries({
                  queryKey: ["authors"],
                  type: "inactive",
                });
                setOffset(0);
                setKeyword("");
                setStatus("");
                setStatusStatic([]);
                setSelectedPageOpt([])
                setTimeout(() => {
                  setStatusStatic([
                    { _id: "", name: "All", color: "success", current: true },
                    {
                      _id: true,
                      name: "Published",
                      color: "success",
                      current: false,
                    },
                    {
                      _id: false,
                      name: "Draft",
                      color: "warning",
                      current: false,
                    },
                  ]);
                }, 300);
              }}
            />
            <Button
              label={"Add Author"}
              labelClasses={""}
              classes={""}
              icon={"fa-regular fa-plus"}
              iconPosition={"left"}
              functions={addModal}
            />
          </div>
        </div>
        <div className="radio-filters">
          <RadioGroup
            data={statusStatic}
            setData={setStatus}
            inputName={"status"}
          />
        </div>
      </div>
      <div className="author-list">
        <AuthorList />
      </div>
      {/* // For add Modal */}
      <Modal
        title={"Add Authors"}
        size={"md"}
        isOpen={isAddModal}
        modalClose={() => setIsAddModal(false)}
      >
        <AddAuthors
          modalClose={() => setIsAddModal(false)}
          query={queryAuthors}
        />
      </Modal>
    </>
  );
};

export default AuthorsList;
